import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Drawer, Tabs } from 'antd';
import { useTranslation } from 'react-i18next';

import { useStores } from '@/hooks/use-stores';
import CloseIcon from '@/comp/common/icons/CloseIcon';
import EPIconButton from '@/components/EPButton/EPIconButton';
import { OnOffEnum, DeviceAction } from '@/constants/enums';
import { getDeviceIconAndLabel } from '@/comp/StationControlCenter/UniversalDeviceWidget/UniversalDeviceWidget.utils';
import Footer from '@/comp/MapBuilder/nodes/nodeWidget/DetailsDrawer/Footer/Footer';
import ActivityIndication from '@/comp/MapBuilder/nodes/nodeWidget/DetailsDrawer/ActivityIndication/ActivityIndication';
import {
  getAssetNameDetails,
  getConnectivityIcon,
} from '@/comp/MapBuilder/nodes/nodeWidget/DetailsDrawer/DetailsDrawer.utils';
import { Entity } from '@/comp/MapBuilder/nodes/nodeWidget/NodeWidget.types';

import UniversalAssetInfo from './Tabs/UniversalAssetInfo/UniversalAssetInfo';
import UniversalAssetImages from './Tabs/UniversalAssetImages/UniversalAssetImages';
import { DrawerContentContainer, Container, FooterSlot } from './UniversalDeviceDrawer.styles';

interface UniversalDeviceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  deviceId: string;
}

const UniversalDeviceDrawer = observer(({ isOpen, onClose, deviceId }: UniversalDeviceDrawerProps) => {
  const { t } = useTranslation();
  const { stationStore, controlCenterStore } = useStores();
  const [activeTab, setActiveTab] = useState('assetInfo');

  const device = stationStore.getDeviceById(deviceId);
  if (!device) return null;

  const { icon, label } = getDeviceIconAndLabel(device.deviceType);
  const currentAsset = stationStore.assetsDevices?.find(asset => asset.deviceId === device.deviceId);
  const deviceDrawerSchema = controlCenterStore.getDeviceDrawerSchemaForDevice(device);

  const tabItems = [
    {
      label: t('assetInfo'),
      key: 'assetInfo',
      children: <UniversalAssetInfo deviceId={deviceId} />,
    },
    {
      label: t('assetImages'),
      key: 'assetImages',
      children: <UniversalAssetImages deviceId={deviceId} />,
    },
  ];
  const hasMaintenanceAction = deviceDrawerSchema?.actions?.some(action => action === DeviceAction.InMaintenance);
  const hasPowerOnOffAction = deviceDrawerSchema?.actions?.some(action => action === DeviceAction.PowerOnOff);
  const shouldShowFooter = Boolean(hasMaintenanceAction || hasPowerOnOffAction);
  const Icon = getConnectivityIcon(device.deviceType, device as unknown as Entity, currentAsset, t);
  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation();

  const inMaintenance = hasMaintenanceAction ? device.inMaintenance : undefined;
  return (
    <Drawer open={isOpen} onClose={onClose} width={664} closeIcon={false} destroyOnHidden>
      <DrawerContentContainer $inMaintenance={device.inMaintenance} onMouseDown={onMouseDown}>
        {device.inMaintenance && <div className="maintenance-banner">{t('Maintenance')}</div>}
        <Container>
          <div className="drawer-details-header-buttons">
            <EPIconButton icon={<CloseIcon />} onClick={onClose} />
          </div>
          <div className="drawer-header">
            <div className="header-left">
              <div className="device-icon-and-type">
                <div className="device-icon">{icon}</div>
                <div className="device-type">{label}</div>
              </div>
              <div className="device-info">
                <div className="device-name">{device.name ?? ''}</div>
                <div className="device-model">{getAssetNameDetails(device)}</div>
              </div>
            </div>
            <div className="header-right">
              {Icon}
              <ActivityIndication entity={device as unknown as Entity} />
            </div>
          </div>

          <Tabs
            items={tabItems}
            activeKey={activeTab}
            onChange={setActiveTab}
            destroyInactiveTabPane={false}
            moreIcon={null}
          />
        </Container>

        {shouldShowFooter && (
          <FooterSlot>
            <Footer
              inMaintenance={inMaintenance as boolean}
              poweredOn={device.poweredOn as OnOffEnum}
              deviceId={device.deviceId}
              deviceType={device.deviceType}
            />
          </FooterSlot>
        )}
      </DrawerContentContainer>
    </Drawer>
  );
});

export default UniversalDeviceDrawer;
